// Check if WebCodecs is supported
const hasWebCodecs = "VideoEncoder" in window;

export async function compressVideo(file: File): Promise<Blob> {
  if (!hasWebCodecs) {
    console.warn("WebCodecs not supported, returning original file");
    return file;
  }

  try {
    // Create video element to get metadata
    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    await new Promise((resolve) => {
      video.onloadedmetadata = resolve;
    });

    // Calculate target dimensions (max 1280p)
    const maxWidth = 1280;
    const scale = Math.min(1, maxWidth / video.videoWidth);
    const targetWidth = Math.floor(video.videoWidth * scale);
    const targetHeight = Math.floor(video.videoHeight * scale);

    // Create canvas for frame extraction
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d")!;

    // Create MediaRecorder with optimized settings
    const stream = canvas.captureStream();
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: "video/webm;codecs=vp9",
      videoBitsPerSecond: 2500000, // 2.5 Mbps
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (e) => chunks.push(e.data);

    // Start recording
    mediaRecorder.start();

    // Process video frames
    const duration = video.duration;
    const fps = 30;
    const totalFrames = Math.floor(duration * fps);

    for (let i = 0; i < totalFrames; i++) {
      video.currentTime = i / fps;
      await new Promise((resolve) => {
        video.onseeked = () => {
          ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
          resolve(null);
        };
      });
    }

    // Stop recording and get compressed video
    mediaRecorder.stop();
    const compressedVideo = await new Promise<Blob>((resolve) => {
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        resolve(blob);
      };
    });

    // Cleanup
    URL.revokeObjectURL(video.src);
    video.remove();
    canvas.remove();

    return compressedVideo;
  } catch (error) {
    console.error("Video compression error:", error);
    throw new Error("Video compression failed");
  }
}

export async function generateThumbnail(file: File): Promise<Blob> {
  try {
    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);

    await new Promise((resolve) => {
      video.onloadedmetadata = resolve;
    });

    // Seek to 1 second
    video.currentTime = 1;
    await new Promise((resolve) => {
      video.onseeked = resolve;
    });

    // Create canvas and draw video frame
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(video, 0, 0);

    // Convert to blob
    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((b) => resolve(b!), "image/jpeg", 0.85);
    });

    // Cleanup
    URL.revokeObjectURL(video.src);
    video.remove();
    canvas.remove();

    return blob;
  } catch (error) {
    console.error("Thumbnail generation error:", error);
    throw new Error("Thumbnail generation failed");
  }
}

export async function getVideoDuration(file: File): Promise<number> {
  try {
    const video = document.createElement("video");
    video.preload = "metadata";

    const duration = await new Promise<number>((resolve) => {
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(Math.round(video.duration));
      };
      video.src = URL.createObjectURL(file);
    });

    video.remove();
    return duration;
  } catch (error) {
    console.error("Error getting video duration:", error);
    return 0;
  }
}

export async function getVideoResolution(file: File): Promise<string> {
  try {
    const video = document.createElement("video");
    video.preload = "metadata";

    const resolution = await new Promise<string>((resolve) => {
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(`${video.videoWidth}x${video.videoHeight}`);
      };
      video.src = URL.createObjectURL(file);
    });

    video.remove();
    return resolution;
  } catch (error) {
    console.error("Error getting video resolution:", error);
    return "Unknown";
  }
}
